import { PixelMark } from "./PixelMark";

/**
 * Personalized fleet board for the result step — the same live-looking dashboard mock as
 * the marketing site's hero (apps/site/src/home/AgentBoard.tsx), with the rows led by the
 * agents the visitor said they run. "The data is the marketing" (design direction).
 */
type RowStatus = "ok" | "wait" | "blocked" | "idle";

type BoardRow = {
  status: RowStatus;
  name: string;
  activity: string;
  time: string;
  cost: string;
};

/** One demo row per known agent; picked agents fill the board in this voice. */
const ROW_TEMPLATES: Record<string, Omit<BoardRow, "status">> = {
  claude_code: { name: "claude code", activity: "refactoring checkout flow", time: "24m10s", cost: "$1.84" },
  codex: { name: "codex", activity: "implementing API endpoint", time: "18m02s", cost: "$1.21" },
  cursor: { name: "cursor", activity: "editing PaymentForm.tsx", time: "12m40s", cost: "$0.74" },
  gemini: { name: "gemini", activity: "writing tests for the parser", time: "9m45s", cost: "$0.31" },
  opencode: { name: "opencode", activity: "running the build", time: "6m12s", cost: "$0.28" },
  openclaw: { name: "openclaw", activity: "booking your flights", time: "4m12s", cost: "$0.42" },
  hermes: { name: "hermes", activity: "triaging your inbox", time: "2m30s", cost: "$0.12" },
  other: { name: "scripts", activity: "nightly data sync", time: "51m02s", cost: "$0.00" },
};

const DEFAULT_FLEET = ["claude_code", "codex", "cursor"];

/** Position-based traffic-light states: working, waiting, blocked, then idle. */
const STATUS_BY_POSITION: RowStatus[] = ["ok", "wait", "blocked", "idle"];

const rowsFor = (agentsUsed?: string): BoardRow[] => {
  const picked = (agentsUsed ?? "").split(",").filter((id) => ROW_TEMPLATES[id]);
  const ids = picked.length > 0 ? picked.slice(0, 3) : DEFAULT_FLEET;
  return ids.map((id, index) => ({
    ...ROW_TEMPLATES[id],
    status: ids.length === 1 ? "ok" : STATUS_BY_POSITION[index % STATUS_BY_POSITION.length],
  }));
};

export function AgentBoard({ agentsUsed }: { agentsUsed?: string }) {
  const rows = rowsFor(agentsUsed);
  const working = rows.filter((row) => row.status === "ok").length;
  const needsAttention = rows.filter((row) => row.status === "wait" || row.status === "blocked").length;
  const summary = `${working} working · ${needsAttention} needs you`;

  return (
    <div
      className="agent-board"
      role="img"
      aria-label={`Agent Island preview: ${rows.length} agents with traffic-light activity states, durations, and costs`}
    >
      <div className="agent-board__titlebar" aria-hidden="true">
        <PixelMark className="agent-board__brand-mark" size={15} />
        <span className="agent-board__title">Agent Island</span>
        <span className="agent-board__summary">{summary}</span>
      </div>
      <div className="agent-board__legend" aria-hidden="true">
        <span><i className="is-ok" />Working</span>
        <span><i className="is-wait" />Waiting</span>
        <span><i className="is-blocked" />Stuck</span>
        <span><i className="is-idle" />Idle</span>
      </div>
      <div className="agent-board__rows" aria-hidden="true">
        {rows.map((row) => (
          <div className={`agent-board__row is-${row.status}`} key={row.name}>
            <span className={`agent-board__pixel is-${row.status}`} />
            <span className="agent-board__name">{row.name}</span>
            <span className="agent-board__activity">{row.activity}</span>
            <span className="agent-board__num">{row.time}</span>
            <span className="agent-board__num">{row.cost}</span>
          </div>
        ))}
      </div>
      <div className="agent-board__foot" aria-hidden="true">
        <span>
          today <strong>$3.86</strong>
        </span>
        <span>
          plan limit <span className="agent-board__hl">~6:00pm</span>
        </span>
      </div>
    </div>
  );
}

/**
 * Menu-bar popover mock for the result step's plan panel — replaces the static PNG
 * (which was mostly empty space). Same mono voice as the board; no status dot noise.
 */
export function MenuBarMock() {
  return (
    <div className="menubar-mock" role="img" aria-label="AgentMax menu bar popover preview">
      <div className="menubar-mock__bar" aria-hidden="true">
        <span className="menubar-mock__app">agentmax</span>
        <span className="menubar-mock__time">9:41 AM</span>
      </div>
      <div className="menubar-mock__popover" aria-hidden="true">
        <div className="menubar-mock__row">
          <span>agents</span>
          <strong>3 working · 1 needs you</strong>
        </div>
        <div className="menubar-mock__row">
          <span>keep Mac awake</span>
          <span className="menubar-mock__on">on</span>
        </div>
      </div>
    </div>
  );
}
